// A Bearer token the API will accept.
//
// Kinde's v3 SDK treats an access token as live until 60s AFTER its `exp`
// (`isJWTActive` adds its leeway the wrong way), while the API rejects it from
// `exp` on. For that minute `getToken()` hands back the expired token instead of
// refreshing it, and every request 401s. The SDK has no public refresh, so a
// token we can see is (nearly) expired is waited out until the SDK refreshes it.

import { getAuthTokenBlocking, type GetTokenFn } from "./getAuthTokenBlocking";

// kinde-auth-pkce-js LEEWAY: getToken() refreshes only past exp + this.
const SDK_LEEWAY_SECONDS = 60;
// Margin for the request's own flight time against the API's expiry check.
const MIN_REMAINING_SECONDS = 1;
// How far behind the API's clock the browser's may run for a retry to wait it out.
const MAX_CLOCK_LAG_SECONDS = 30;

const tokenExpiry = (token: string): number | null => {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
};

const nearlyExpired = (token: string): boolean => {
  const exp = tokenExpiry(token);
  return exp !== null && exp - Date.now() / 1000 <= MIN_REMAINING_SECONDS;
};

// One wait-and-refresh for every caller: the refresh token is single-use, so
// parallel refreshes would race and all but one would fail.
let pending: Promise<string> | null = null;

const refreshPast = (token: string, getToken: GetTokenFn): Promise<string> => {
  if (pending === null) {
    const exp = tokenExpiry(token) ?? 0;
    // The SDK refreshes once its whole-second clock reaches exp + leeway.
    const waitMs = Math.max(0, (exp + SDK_LEEWAY_SECONDS) * 1000 + 250 - Date.now());
    pending = new Promise<void>((resolve) => setTimeout(resolve, waitMs))
      .then(() => getAuthTokenBlocking(getToken, undefined))
      .finally(() => {
        pending = null;
      });
  }
  return pending;
};

export const getFreshToken = async (getToken: GetTokenFn): Promise<string> => {
  // Join a refresh already under way rather than letting the SDK start another.
  if (pending !== null) return pending;
  const token = await getAuthTokenBlocking(getToken, undefined);
  return nearlyExpired(token) ? refreshPast(token, getToken) : token;
};

// After the API rejected `rejected`: a token to retry with, or null when there
// is nothing different to send. Covers a browser clock that runs behind the
// API's, which the expiry check above cannot see.
export const getTokenAfterRejection = async (
  rejected: string,
  getToken: GetTokenFn,
): Promise<string | null> => {
  if (pending !== null) {
    const joined = await pending;
    return joined === rejected ? null : joined;
  }
  const token = await getAuthTokenBlocking(getToken, undefined);
  if (token !== rejected) return token;
  // Rejected well before its expiry: not a clock problem, and waiting would not help.
  const exp = tokenExpiry(rejected);
  if (exp === null || exp - Date.now() / 1000 > MAX_CLOCK_LAG_SECONDS) return null;
  const refreshed = await refreshPast(rejected, getToken);
  return refreshed === rejected ? null : refreshed;
};
